import { api } from '../api.js';
import { loadOperatorSession, clearOperatorSession } from '../config.js';
import { escapeHtml, qs } from '../dom.js';
import { loadTheme, toggleTheme } from '../theme.js';

/**
 * Phone home menu (Suri, 2026-10-15): the ASN list used to double as the home screen with every
 * job's button squeezed into its top bar. Each job now has its own tile; ASNs are just one of them.
 */
const TILES: { id: string; label: string; hint: string; hash: string }[] = [
  { id: 'receiving', label: 'Receiving', hint: 'ASNs · book in containers', hash: '#/asns' },
  { id: 'picking', label: 'Picking', hint: 'Pick runs and orders', hash: '#/pick' },
  { id: 'replen', label: 'Replen', hint: 'Fill low pick shelves', hash: '#/replen' },
  { id: 'stockCheck', label: 'Stock Check', hint: 'Look up a product or shelf', hash: '#/stock-lookup' },
  { id: 'stockCount', label: 'Location Count', hint: 'Count one shelf', hash: '#/stock-count' },
  { id: 'skuCount', label: 'SKU Count', hint: 'Scan a SKU, count the total', hash: '#/sku-count' },
  { id: 'settings', label: 'Settings', hint: 'Device and server', hash: '#/settings' },
];

/** Small inline line icons (no image files, no emoji font: they draw instantly and work offline). */
const ICONS: Record<string, string> = {
  receiving: '<path d="M3 7l9-4 9 4v10l-9 4-9-4z"/><path d="M3 7l9 4 9-4M12 11v10"/>',
  picking: '<path d="M9 6h11M9 12h11M9 18h11"/><path d="M3.5 6l1.2 1.2L7 5M3.5 12l1.2 1.2L7 11M3.5 18l1.2 1.2L7 17"/>',
  replen: '<path d="M20 11a8 8 0 0 0-14-4M4 13a8 8 0 0 0 14 4"/><path d="M20 4v5h-5M4 20v-5h5"/>',
  stockCheck: '<circle cx="11" cy="11" r="6"/><path d="M16 16l5 5"/>',
  stockCount: '<rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4h6v3H9zM9 12h6M9 16h4"/>',
  skuCount: '<path d="M4 6v12M7 6v12M11 6v12M14 6v12M18 6v12M20 6v12"/>',
  settings: '<path d="M4 7h10M18 7h2M4 17h2M10 17h10"/><circle cx="16" cy="7" r="2"/><circle cx="8" cy="17" r="2"/>',
  release: '<circle cx="9" cy="8" r="3"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><path d="M17 8v6M14 11h6"/>',
};
function tileInner(id: string, label: string, hint: string): string {
  return `<span class="home-tile-icon"><svg viewBox="0 0 24 24" aria-hidden="true">${ICONS[id] ?? ''}</svg></span><span class="home-tile-label">${label}</span><span class="home-tile-hint">${hint}</span>`;
}

export async function renderHome(container: HTMLElement): Promise<void> {
  const session = loadOperatorSession();
  const div = document.createElement('div');
  div.className = 'screen';
  div.innerHTML = `
    <div class="topbar"><h1>Warehouse</h1><button class="ghost" id="theme"></button></div>
    ${session ? `<p class="hint">Logged in as <strong>${escapeHtml(session.name)}</strong> · <button class="link-button" id="logout">Log out</button></p>` : ''}
    <div class="home-grid">
      ${TILES.filter((t) => t.id !== 'settings' || session?.role !== 'OPERATOR').map((t) => `<button type="button" class="home-tile" id="tile-${t.id}">${tileInner(t.id, t.label, t.hint)}</button>`).join('')}
    </div>
  `;
  container.appendChild(div);
  // Only the few logins ticked "Release batches" on the admin Operators page get this tile (the Worker checks again on every call).
  void api.me().then((me) => {
    const grid = div.querySelector<HTMLElement>('.home-grid');
    if (!me.canRelease || !grid || grid.querySelector('#tile-release')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'home-tile';
    btn.id = 'tile-release';
    btn.innerHTML = tileInner('release', 'Batches', me.shortOpen && me.shortOpen > 0 ? `${me.shortOpen} short order${me.shortOpen === 1 ? '' : 's'} to deal with` : 'Give batches to pickers');
    if (me.shortOpen && me.shortOpen > 0) btn.classList.add('has-alert');
    btn.addEventListener('click', () => {
      location.hash = '#/release';
    });
    const settings = grid.querySelector('#tile-settings');
    if (settings) grid.insertBefore(btn, settings);
    else grid.appendChild(btn);
  }, () => undefined);
  const themeBtn = qs<HTMLButtonElement>(div, '#theme');
  const drawTheme = () => {
    themeBtn.textContent = loadTheme() === 'light' ? '🌙 Dark' : '☀ Light';
  };
  drawTheme();
  themeBtn.addEventListener('click', () => {
    toggleTheme();
    drawTheme();
  });
  for (const t of TILES) {
    div.querySelector<HTMLButtonElement>(`#tile-${t.id}`)?.addEventListener('click', () => {
      location.hash = t.hash;
    });
  }
  div.querySelector<HTMLButtonElement>('#logout')?.addEventListener('click', () => {
    void (async () => {
      try {
        await api.logout();
      } catch {
        // Best-effort — clear locally regardless.
      }
      clearOperatorSession();
      location.hash = '#/login';
    })();
  });
}
